import React, { useState } from 'react';
import {
    SectionContainer,
    ComponentPreview,
    SubSection,
    PropsTable,
    BrowserCompatibility,
} from '../Content';
import { Breadcrumb } from '../Breadcrumb';
import CodeBlock from '../CodeBlock';
import { PropDef } from '../../../types';
import { Header } from '../Header';
import { Button } from '../Button';
import { Checkbox } from '../Checkbox';
import { Input } from '../Input';
import { Select } from '../Select';
import { Card } from '../Card';
import { Icon } from '../icons';

// Mock data for demonstration
const mockLinks = [
    { href: '#home', label: 'Home' },
    { href: '#about', label: 'About' },
    { href: '#services', label: 'Services' },
    { href: '#contact', label: 'Contact' }
];

const mockLanguages = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'it', name: 'Italiano', flag: '🇮🇹' },
    { code: 'pt', name: 'Português', flag: '🇵🇹' },
    { code: 'zh', name: '中文', flag: '🇨🇳' },
    { code: 'ja', name: '日本語', flag: '🇯🇵' }
];

const mockUserMenuLinks = [
    { href: '#profile', label: 'Profile', icon: 'person' },
    { href: '#settings', label: 'Settings', icon: 'settings' },
    { href: '#billing', label: 'Billing', icon: 'credit_card' },
    { href: '#support', label: 'Support', icon: 'help' }
];

const mockUser = {
    name: 'John Doe',
    email: 'john.doe@example.com',
    role: 'Administrator',
    avatarUrl: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80',
    isOnline: true
};

const mockNotifications = [
    {
        id: '1',
        title: 'New message received',
        message: 'You have a new message from Sarah Johnson about the project update.',
        time: '2 minutes ago',
        read: false,
        type: 'info' as const
    },
    {
        id: '2',
        title: 'Task completed',
        message: 'The data migration task has been completed successfully.',
        time: '1 hour ago',
        read: false,
        type: 'success' as const
    },
    {
        id: '3',
        title: 'System maintenance',
        message: 'Scheduled maintenance will begin at 2:00 AM EST.',
        time: '3 hours ago',
        read: true,
        type: 'warning' as const
    }
];

const headerProps: PropDef[] = [
    { name: 'logo', type: '{ src?: string; text?: string; href?: string }', default: 'undefined', description: 'Logo configuration object' },
    { name: 'links', type: 'HeaderLink[]', default: '[]', description: 'Navigation links to display' },
    { name: 'showSearch', type: 'boolean', default: 'false', description: 'Show search bar' },
    { name: 'searchPlaceholder', type: 'string', default: '"Search..."', description: 'Search input placeholder text' },
    { name: 'onSearch', type: '(query: string) => void', default: 'undefined', description: 'Search callback function' },
    { name: 'showNotifications', type: 'boolean', default: 'false', description: 'Show notification icon with dropdown' },
    { name: 'notifications', type: 'NotificationItem[]', default: '[]', description: 'Array of notification items' },
    { name: 'user', type: 'User | undefined', default: 'undefined', description: 'Current user object with name, email, role, avatarUrl' },
    { name: 'userMenuLinks', type: 'UserMenuLink[]', default: '[]', description: 'Links for user dropdown menu' },
    { name: 'variant', type: '"website" | "dashboard"', default: '"website"', description: 'Header layout variant' },
    { name: 'sticky', type: 'boolean', default: 'false', description: 'Make header sticky to top' },
    { name: 'elevated', type: 'boolean', default: 'false', description: 'Add shadow elevation effect' },
    { name: 'onLogin', type: '() => void', default: '() => {}', description: 'Callback for login button click' },
    { name: 'onSignUp', type: '() => void', default: '() => {}', description: 'Callback for sign up button click' },
    { name: 'onLogout', type: '() => void', default: '() => {}', description: 'Callback for logout action' }
];

export const HeaderSection: React.FC<{ groupTitle: string; sectionTitle: string; id: string; }> = ({ groupTitle, sectionTitle, id }) => {
    // Workbench state
    const [showWorkbench, setShowWorkbench] = useState(false);
    
    // Header configuration state
    const [config, setConfig] = useState({
        // Logo settings
        showLogo: true,
        logoText: 'Acutrack',
        logoHref: '#',
        
        // Navigation settings
        showNavigation: true,
        
        // Search settings
        showSearch: false,
        searchPlaceholder: 'Search...',
        
        // Notification settings
        showNotifications: false,
        
        // User settings
        showUser: true,
        userLoggedIn: false,
        userOnlineStatus: true,
        
        // Language settings
        showLanguageSelector: false,
        currentLanguage: 'en',
        
        // Layout settings
        variant: 'website' as 'website' | 'dashboard',
        sticky: false,
        elevated: false
    });

    const handleConfigChange = (key: string, value: any) => {
        setConfig(prev => ({ ...prev, [key]: value }));
    };

    const handleSearch = (query: string) => {
        console.log('Search query:', query);
        alert(`Searching for: "${query}"`);
    };

    const handleNotificationClick = (notification: any) => {
        console.log('Notification clicked:', notification);
        alert(`Clicked notification: ${notification.title}`);
    };

    const handleMarkAllRead = () => {
        console.log('Mark all notifications as read');
        alert('All notifications marked as read');
    };

    const generateCode = () => {
        const props = [];
        
        if (config.showLogo && config.logoText !== 'Acutrack') {
            props.push(`logo={{ text: "${config.logoText}", href: "${config.logoHref}" }}`);
        }
        
        if (config.showNavigation) {
            props.push('links={navigationLinks}');
        }
        
        if (config.showSearch) {
            props.push('showSearch={true}');
            if (config.searchPlaceholder !== 'Search...') {
                props.push(`searchPlaceholder="${config.searchPlaceholder}"`);
            }
            props.push('onSearch={handleSearch}');
        }
        
        if (config.showNotifications) {
            props.push('showNotifications={true}');
            props.push('notifications={notifications}');
            props.push('onNotificationClick={handleNotificationClick}');
            props.push('onMarkAllRead={handleMarkAllRead}');
        }
        
        if (config.showUser && config.userLoggedIn) {
            props.push('user={currentUser}');
            props.push('userMenuLinks={userMenuLinks}');
            props.push('onLogout={handleLogout}');
        } else if (config.showUser && !config.userLoggedIn) {
            props.push('onLogin={handleLogin}');
            props.push('onSignUp={handleSignUp}');
        }
        
        if (config.variant !== 'website') {
            props.push(`variant="${config.variant}"`);
        }
        
        if (config.sticky) {
            props.push('sticky={true}');
        }
        
        if (config.elevated) {
            props.push('elevated={true}');
        }

        return `import { Header } from './components/Header';

const navigationLinks = [
    { href: '#home', label: 'Home' },
    { href: '#about', label: 'About' },
    { href: '#services', label: 'Services' },
    { href: '#contact', label: 'Contact' }
];

const userMenuLinks = [
    { href: '#profile', label: 'Profile', icon: 'person' },
    { href: '#settings', label: 'Settings', icon: 'settings' },
    { href: '#billing', label: 'Billing', icon: 'credit_card' },
    { href: '#support', label: 'Support', icon: 'help' }
];

const currentUser = {
    name: 'John Doe',
    email: 'john.doe@example.com',
    role: 'Administrator',
    avatarUrl: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e'
};

const notifications = [
    {
        id: '1',
        title: 'New message received',
        message: 'You have a new message from Sarah Johnson.',
        time: '2 minutes ago',
        read: false
    }
];

<Header
    ${props.join('\n    ')}
/>`;
    };

    const currentHeaderProps = {
        ...(config.showLogo && { 
            logo: { 
                text: config.logoText, 
                href: config.logoHref 
            } 
        }),
        ...(config.showNavigation && { links: mockLinks }),
        ...(config.showSearch && { 
            showSearch: true,
            searchPlaceholder: config.searchPlaceholder,
            onSearch: handleSearch
        }),
        ...(config.showNotifications && { 
            showNotifications: true,
            notifications: mockNotifications,
            onNotificationClick: handleNotificationClick,
            onMarkAllRead: handleMarkAllRead
        }),
        ...(config.showUser && config.userLoggedIn && { 
            user: {
                ...mockUser,
                isOnline: config.userOnlineStatus
            },
            userMenuLinks: mockUserMenuLinks,
            onLogout: () => alert('Logout clicked')
        }),
        ...(config.showLanguageSelector && {
            showLanguageSelector: true,
            languages: mockLanguages,
            currentLanguage: config.currentLanguage,
            onLanguageChange: (lang: string) => handleConfigChange('currentLanguage', lang)
        }),
        ...(config.showUser && !config.userLoggedIn && { 
            onLogin: () => alert('Login clicked'),
            onSignUp: () => alert('Sign Up clicked')
        }),
        variant: config.variant,
        sticky: config.sticky,
        elevated: config.elevated
    };

    return (
        <SectionContainer id={id}>
            <Breadcrumb group={groupTitle} section={sectionTitle} />
            <h2 className="section-title">Header</h2>
            <p className="section-subtitle">
                A comprehensive header component with user avatar dropdown, notifications, search functionality, and flexible configuration options. Perfect for both websites and dashboard applications with responsive design.
            </p>

            <SubSection title="Interactive Workbench">
                <div className="space-y-6">
                    <div className="flex items-center justify-between">
                        <p className="text-text-secondary dark:text-dark-text-secondary">
                            Customize the header component with the controls below to see how different configurations affect the appearance and functionality.
                        </p>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setShowWorkbench(!showWorkbench)}
                            leftIcon={<Icon>{showWorkbench ? 'visibility_off' : 'tune'}</Icon>}
                        >
                            {showWorkbench ? 'Hide' : 'Show'} Controls
                        </Button>
                    </div>

                    {showWorkbench && (
                        <Card className="p-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {/* Logo Settings */}
                                <div className="space-y-4">
                                    <h4 className="font-semibold text-text-primary dark:text-dark-text-primary">Logo Settings</h4>
                                    <Checkbox
                                        id="showLogo"
                                        label="Show Logo"
                                        checked={config.showLogo}
                                        onChange={(e) => handleConfigChange('showLogo', e.target.checked)}
                                    />
                                    {config.showLogo && (
                                        <>
                                            <Input
                                                id="logoText"
                                                label="Logo Text"
                                                value={config.logoText}
                                                onChange={(e) => handleConfigChange('logoText', e.target.value)}
                                            />
                                            <Input
                                                id="logoHref"
                                                label="Logo Link"
                                                value={config.logoHref}
                                                onChange={(e) => handleConfigChange('logoHref', e.target.value)}
                                            />
                                        </>
                                    )}
                                </div>

                                {/* Navigation Settings */}
                                <div className="space-y-4">
                                    <h4 className="font-semibold text-text-primary dark:text-dark-text-primary">Navigation</h4>
                                    <Checkbox
                                        id="showNavigation"
                                        label="Show Navigation Links"
                                        checked={config.showNavigation}
                                        onChange={(e) => handleConfigChange('showNavigation', e.target.checked)}
                                    />
                                </div>

                                {/* Search Settings */}
                                <div className="space-y-4">
                                    <h4 className="font-semibold text-text-primary dark:text-dark-text-primary">Search</h4>
                                    <Checkbox
                                        id="showSearch"
                                        label="Show Search Bar"
                                        checked={config.showSearch}
                                        onChange={(e) => handleConfigChange('showSearch', e.target.checked)}
                                    />
                                    {config.showSearch && (
                                        <Input
                                            id="searchPlaceholder"
                                            label="Search Placeholder"
                                            value={config.searchPlaceholder}
                                            onChange={(e) => handleConfigChange('searchPlaceholder', e.target.value)}
                                        />
                                    )}
                                </div>

                                {/* Notification Settings */}
                                <div className="space-y-4">
                                    <h4 className="font-semibold text-text-primary dark:text-dark-text-primary">Notifications</h4>
                                    <Checkbox
                                        id="showNotifications"
                                        label="Show Notifications"
                                        checked={config.showNotifications}
                                        onChange={(e) => handleConfigChange('showNotifications', e.target.checked)}
                                    />
                                </div>

                                {/* User Settings */}
                                <div className="space-y-4">
                                    <h4 className="font-semibold text-text-primary dark:text-dark-text-primary">User</h4>
                                    <Checkbox
                                        id="showUser"
                                        label="Show User Section"
                                        checked={config.showUser}
                                        onChange={(e) => handleConfigChange('showUser', e.target.checked)}
                                    />
                                    {config.showUser && (
                                        <>
                                            <Checkbox
                                                id="userLoggedIn"
                                                label="User Logged In"
                                                checked={config.userLoggedIn}
                                                onChange={(e) => handleConfigChange('userLoggedIn', e.target.checked)}
                                            />
                                            {config.userLoggedIn && (
                                                <Checkbox
                                                    id="userOnlineStatus"
                                                    label="Show Online Status"
                                                    checked={config.userOnlineStatus}
                                                    onChange={(e) => handleConfigChange('userOnlineStatus', e.target.checked)}
                                                />
                                            )}
                                        </>
                                    )}
                                </div>

                                {/* Language Settings */}
                                <div className="space-y-4">
                                    <h4 className="font-semibold text-text-primary dark:text-dark-text-primary">Language</h4>
                                    <Checkbox
                                        id="showLanguageSelector"
                                        label="Show Language Selector"
                                        checked={config.showLanguageSelector}
                                        onChange={(e) => handleConfigChange('showLanguageSelector', e.target.checked)}
                                    />
                                    {config.showLanguageSelector && (
                                        <Select
                                            id="currentLanguage"
                                            label="Current Language"
                                            value={config.currentLanguage}
                                            onChange={(value) => handleConfigChange('currentLanguage', value)}
                                            options={mockLanguages.map(lang => ({
                                                value: lang.code,
                                                label: `${lang.flag} ${lang.name}`
                                            }))}
                                        />
                                    )}
                                </div>

                                {/* Layout Settings */}
                                <div className="space-y-4">
                                    <h4 className="font-semibold text-text-primary dark:text-dark-text-primary">Layout</h4>
                                    <Select
                                        id="variant"
                                        label="Variant"
                                        value={config.variant}
                                        onChange={(value) => handleConfigChange('variant', value)}
                                        options={[
                                            { value: 'website', label: 'Website' },
                                            { value: 'dashboard', label: 'Dashboard' }
                                        ]}
                                    />
                                    <Checkbox
                                        id="sticky"
                                        label="Sticky Header"
                                        checked={config.sticky}
                                        onChange={(e) => handleConfigChange('sticky', e.target.checked)}
                                    />
                                    <Checkbox
                                        id="elevated"
                                        label="Elevated (Shadow)"
                                        checked={config.elevated}
                                        onChange={(e) => handleConfigChange('elevated', e.target.checked)}
                                    />
                                </div>
                            </div>
                        </Card>
                    )}

                    {/* Live Preview */}
                    <div>
                        <h4 className="font-semibold text-text-primary dark:text-dark-text-primary mb-4">Live Preview</h4>
                        <ComponentPreview className="!p-0 !bg-transparent !border-none" allowOverlays={true}>
                            <div className="w-full rounded-lg border border-border-color dark:border-dark-border-color overflow-hidden">
                                <Header {...currentHeaderProps} />
                                <div className="p-8 bg-bg-secondary dark:bg-dark-bg-secondary text-center">
                                    <p className="text-text-secondary dark:text-dark-text-secondary">
                                        Page content would go here...
                                    </p>
                                </div>
                            </div>
                        </ComponentPreview>
                    </div>

                    {/* Generated Code */}
                    <div>
                        <h4 className="font-semibold text-text-primary dark:text-dark-text-primary mb-4">Generated Code</h4>
                        <CodeBlock code={generateCode()} />
                    </div>
                </div>
            </SubSection>

            <SubSection title="Preset Examples">
                <div className="space-y-8">
                    {/* Website Header */}
                    <div>
                        <h4 className="font-semibold text-text-primary dark:text-dark-text-primary mb-4">Website Header (Logged Out)</h4>
                        <ComponentPreview className="!p-0 !bg-transparent !border-none" allowOverlays={true}>
                            <div className="w-full rounded-lg border border-border-color dark:border-dark-border-color overflow-hidden">
                                <Header
                                    logo={{ text: 'Acutrack', href: '#' }}
                                    links={mockLinks}
                                    showSearch={true}
                                    onSearch={handleSearch}
                                    onLogin={() => alert('Login clicked')}
                                    onSignUp={() => alert('Sign Up clicked')}
                                    variant="website"
                                />
                                <div className="p-4 bg-bg-secondary dark:bg-dark-bg-secondary text-center text-sm">
                                    Website content...
                                </div>
                            </div>
                        </ComponentPreview>
                    </div>

                    {/* Dashboard Header */}
                    <div>
                        <h4 className="font-semibold text-text-primary dark:text-dark-text-primary mb-4">Dashboard Header (Logged In)</h4>
                        <ComponentPreview className="!p-0 !bg-transparent !border-none" allowOverlays={true}>
                            <div className="w-full rounded-lg border border-border-color dark:border-dark-border-color overflow-hidden">
                                <Header
                                    logo={{ text: 'Dashboard', href: '#' }}
                                    showSearch={true}
                                    searchPlaceholder="Search dashboard..."
                                    onSearch={handleSearch}
                                    showNotifications={true}
                                    notifications={mockNotifications}
                                    onNotificationClick={handleNotificationClick}
                                    onMarkAllRead={handleMarkAllRead}
                                    showLanguageSelector={true}
                                    languages={mockLanguages}
                                    currentLanguage="en"
                                    onLanguageChange={(lang) => console.log('Language changed to:', lang)}
                                    user={mockUser}
                                    userMenuLinks={mockUserMenuLinks}
                                    onLogout={() => alert('Logout clicked')}
                                    variant="dashboard"
                                    elevated={true}
                                />
                                <div className="p-4 bg-bg-secondary dark:bg-dark-bg-secondary text-center text-sm">
                                    Dashboard content...
                                </div>
                            </div>
                        </ComponentPreview>
                    </div>
                </div>
            </SubSection>

            <SubSection title="Features">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                        <h4 className="font-semibold text-text-primary dark:text-dark-text-primary">🔍 Search Functionality</h4>
                        <p className="text-text-secondary dark:text-dark-text-secondary text-sm">
                            Built-in search bar with customizable placeholder and search callback. Responsive design that adapts to mobile layouts.
                        </p>
                    </div>
                    <div className="space-y-4">
                        <h4 className="font-semibold text-text-primary dark:text-dark-text-primary">🔔 Smart Notifications</h4>
                        <p className="text-text-secondary dark:text-dark-text-secondary text-sm">
                            Notification dropdown with unread count badge, mark all as read functionality, and rich notification content.
                        </p>
                    </div>
                    <div className="space-y-4">
                        <h4 className="font-semibold text-text-primary dark:text-dark-text-primary">👤 User Avatar Menu</h4>
                        <p className="text-text-secondary dark:text-dark-text-secondary text-sm">
                            Comprehensive user dropdown with avatar, user info, custom menu links, and logout functionality.
                        </p>
                    </div>
                    <div className="space-y-4">
                        <h4 className="font-semibold text-text-primary dark:text-dark-text-primary">📱 Responsive Design</h4>
                        <p className="text-text-secondary dark:text-dark-text-secondary text-sm">
                            Fully responsive with mobile-optimized navigation, collapsible menu, and touch-friendly interactions.
                        </p>
                    </div>
                    <div className="space-y-4">
                        <h4 className="font-semibold text-text-primary dark:text-dark-text-primary">🎨 Flexible Variants</h4>
                        <p className="text-text-secondary dark:text-dark-text-secondary text-sm">
                            Website and dashboard variants with different layouts, sticky positioning, and elevation options.
                        </p>
                    </div>
                    <div className="space-y-4">
                        <h4 className="font-semibold text-text-primary dark:text-dark-text-primary">⚙️ Configurable Logo</h4>
                        <p className="text-text-secondary dark:text-dark-text-secondary text-sm">
                            Support for custom logo images, text, and links. Fallback to default Acutrack branding when not specified.
                        </p>
                    </div>
                </div>
            </SubSection>

            <SubSection title="Props Reference">
                <PropsTable data={headerProps} />
            </SubSection>

            <BrowserCompatibility>
                The header uses modern CSS features like <code>backdrop-filter</code> for the blur effect, which may not be supported in older browsers but will gracefully degrade to a solid background.
            </BrowserCompatibility>
        </SectionContainer>
    );
};